import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { Lock, Star } from "lucide-react";

interface AchievementBadgeProps {
  achievement: {
    name: string;
    description: string;
    icon: string;
    badge_color: string;
    points: number;
    requirement_count?: number;
  };
  isUnlocked: boolean;
  progress?: number;
  size?: "sm" | "md" | "lg";
  showProgress?: boolean;
  className?: string;
}

export function AchievementBadge({
  achievement,
  isUnlocked,
  progress = 0,
  size = "md",
  showProgress = false,
  className,
}: AchievementBadgeProps) {
  const sizeClasses = {
    sm: "p-3",
    md: "p-4",
    lg: "p-6",
  };

  const iconSizes = {
    sm: "text-2xl",
    md: "text-3xl",
    lg: "text-4xl",
  };

  const progressPercentage = achievement.requirement_count
    ? (progress / achievement.requirement_count) * 100
    : 0;

  return (
    <Card
      className={cn(
        "relative overflow-hidden transition-all duration-300",
        sizeClasses[size],
        isUnlocked
          ? "border-2 hover:scale-105 hover:shadow-lg animate-scale-in"
          : "opacity-60 hover:opacity-80",
        className
      )}
      style={{
        borderColor: isUnlocked ? achievement.badge_color : "hsl(var(--border))",
      }}
    >
      {/* Background glow effect quando desbloqueado */}
      {isUnlocked && (
        <div
          className="absolute inset-0 opacity-10"
          style={{
            background: `radial-gradient(circle, ${achievement.badge_color} 0%, transparent 70%)`,
          }}
        />
      )}

      <div className="relative flex flex-col items-center gap-3 text-center">
        {/* Ícone */}
        <div
          className={cn(
            "relative flex items-center justify-center w-16 h-16 rounded-full",
            isUnlocked ? "animate-pulse-slow" : ""
          )}
          style={{
            backgroundColor: isUnlocked
              ? `${achievement.badge_color}20`
              : "hsl(var(--muted))",
          }}
        >
          {isUnlocked ? (
            <span className={iconSizes[size]}>{achievement.icon}</span>
          ) : (
            <Lock className="h-6 w-6 text-muted-foreground" />
          )}

          {/* Badge de novo */}
          {isUnlocked && (
            <div className="absolute -top-1 -right-1">
              <Star className="h-5 w-5 text-yellow-500 animate-pulse" />
            </div>
          )}
        </div>

        {/* Nome */}
        <div>
          <h3 className="font-bold text-sm">
            {achievement.name}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {achievement.description}
            {!isUnlocked && achievement.requirement_count && (
              <span className="block mt-1 text-xs font-medium text-primary">
                🎯 Progresso: {progress}/{achievement.requirement_count}
              </span>
            )}
          </p>
        </div>

        {/* Pontos */}
        <Badge
          variant={isUnlocked ? "default" : "secondary"}
          className="text-xs"
          style={{
            backgroundColor: isUnlocked ? achievement.badge_color : undefined,
          }}
        >
          +{achievement.points} pts
        </Badge>

        {/* Barra de progresso */}
        {showProgress && !isUnlocked && achievement.requirement_count && (
          <div className="w-full space-y-1">
            <Progress value={progressPercentage} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {progress}/{achievement.requirement_count}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
